import { Currency } from '@/constants/currency';
import { AnalyticsContext, AnalyticsEventName } from './constants';
import { trackEvent } from './utils';
import {
  Address,
  CheckoutResponse,
  ICartViewModel,
  ItemAnalyticsModel,
  ItemListAnalyticsModel,
  OrderLineViewModel
} from '@/@types/client-api';
import {
  ConvertCartToItemAnalyticsModels,
  ConvertItemAnalyticsModel,
  ConvertItemAnalyticsModelBase
} from './analyticsUtils';
import { IPaymentOption } from '@/components/ui/CheckoutAndOrderManagement/hooks/useCheckoutHook';

const generateEcommerceObject = (data: object) => ({
  ecommerce: { ...data }
});

const LAST_SELECT_ITEM_CONTEXT_KEY = 'last_select_item_context';

type LastSelectItemContext = {
  itemId?: string;
  listId?: string;
  listName?: string;
  index?: number;
  timestamp: number;
};

export interface PromoEvent {
  creative_name?: string;
  creative_slot?: string;
  location_id?: string;
  promotion_id?: string;
  promotion_name?: string;
  context?: AnalyticsContext;
}

export interface PlaceOrderEvent {
  cart?: ICartViewModel;
  context?: AnalyticsContext;
  response?: CheckoutResponse;
  shippingAddress?: Address;
  upkeep?: boolean;
  isStoredInstrument?: boolean;
  saveForFutureUse?: boolean;
  isPaymentRequired?: boolean;
}

export const trackSelectItem = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  context?: AnalyticsContext,
  listId?: string,
  listName?: string,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  if (itemAnalyticsModel && context) {
    const resolvedListId = listId || itemListAnalyticsModel?.listId || '';
    const resolvedListName = listName || itemListAnalyticsModel?.listName || '';
    const resolvedItemIndex = typeof itemIndex === 'number' && itemIndex >= 0 ? itemIndex : 0;

    const selectContext: LastSelectItemContext = {
      itemId: itemAnalyticsModel?.id ?? undefined,
      listId: resolvedListId,
      listName: resolvedListName,
      index: resolvedItemIndex,
      timestamp: Date.now()
    };

    try {
      sessionStorage.setItem(LAST_SELECT_ITEM_CONTEXT_KEY, JSON.stringify(selectContext));
    } catch {
      // no-op
    }

    trackEvent({
      eventName: AnalyticsEventName.SelectItem,
      parameters: {
        context: context || '',
        ...generateEcommerceObject({
          item_list_id: resolvedListId,
          item_list_name: resolvedListName,
          ...ConvertItemAnalyticsModel(
            itemAnalyticsModel,
            1,
            itemListAnalyticsModel || {
              listId: listId,
              listName: listName
            },
            resolvedItemIndex
          )
        })
      }
    });
  }
};

type TrackAddToWishlistEventProps = {
  productCode?: string;
  context?: AnalyticsContext;
  title?: string;
  value?: number;
};

export const trackAddToWishlist = ({
  productCode,
  context,
  title = '',
  value = 0
}: TrackAddToWishlistEventProps) => {
  if (productCode && context) {
    trackEvent({
      eventName: AnalyticsEventName.AddToWishlist,
      parameters: {
        productCode,
        context,
        value,
        currency: Currency.USD,
        items: [
          {
            item_id: productCode,
            item_name: title,
            index: 1
          }
        ]
      }
    });
  }
};

export const trackAddToCart = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  context?: AnalyticsContext,
  transactionId?: string,
  itemListAnalyticsModel?: ItemListAnalyticsModel
) => {
  if (itemAnalyticsModel) {
    trackEvent({
      eventName: AnalyticsEventName.AddToCart,
      parameters: {
        ...generateEcommerceObject({
          value: itemAnalyticsModel?.price || 0,
          transaction_id: transactionId || '',
          currency: Currency.USD,
          items: [ConvertItemAnalyticsModelBase(itemAnalyticsModel, itemListAnalyticsModel)]
        }),
        context: context || ''
      }
    });
  }
};

export const trackBeginCheckoutEvent = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  context?: AnalyticsContext,
  transactionId?: string,
  itemListAnalyticsModel?: ItemListAnalyticsModel
) => {
  if (itemAnalyticsModel) {
    trackEvent({
      eventName: AnalyticsEventName.BeginCheckout,
      parameters: {
        ...generateEcommerceObject({
          transaction_id: transactionId || '',
          items: [ConvertItemAnalyticsModelBase(itemAnalyticsModel, itemListAnalyticsModel)]
        }),
        context: context || ''
      }
    });
  }
};

export const trackPurchaseEvent = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  context?: AnalyticsContext,
  transactionId?: string,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  isStoredInstrument?: boolean
) => {
  if (itemAnalyticsModel) {
    trackEvent({
      eventName: AnalyticsEventName.Purchase,
      parameters: {
        ...generateEcommerceObject({
          transaction_id: transactionId || '',
          items: [ConvertItemAnalyticsModelBase(itemAnalyticsModel, itemListAnalyticsModel)]
        }),
        context: context || '',
        purchase_source:
          itemAnalyticsModel?.price && itemAnalyticsModel?.price > 0 ? 'Checkout' : 'WithRequest',
        stored_instrument: isStoredInstrument ?? false
      }
    });
  }
};

export const trackViewCartEvent = (cart?: ICartViewModel, context?: AnalyticsContext) => {
  if ((cart?.orderLines?.length ?? 0) > 0) {
    trackEvent({
      eventName: AnalyticsEventName.ViewCart,
      parameters: {
        ...generateEcommerceObject({
          items: ConvertCartToItemAnalyticsModels(cart),
          value: cart?.subTotal,
          currency: Currency.USD
        }),
        context: context || ''
      }
    });
  }
};

export const trackSelectCartEvent = (context?: AnalyticsContext) => {
  if (context) {
    trackEvent({
      eventName: AnalyticsEventName.SelectCart,
      parameters: {
        context: context
      }
    });
  }
};

export const trackPrepareCheckout = (cart?: ICartViewModel, transactionId?: string) => {
  if ((cart?.orderLines?.length ?? 0) > 0) {
    const analytics =
      cart?.orderLines?.map(x => x.itemAnalyticsModel).filter(x => x !== undefined) ?? [];

    trackEvent({
      eventName: AnalyticsEventName.BeginCheckout,
      parameters: {
        ...generateEcommerceObject({
          items: ConvertCartToItemAnalyticsModels(cart),
          transaction_id: transactionId ?? '',
          value: cart?.subTotal,
          currency: Currency.USD,
          coupon: analytics.find(x => x.coupon)?.coupon ?? ''
        }),
        context: AnalyticsContext.CheckoutPage
      }
    });
  }
};

export const trackAddPaymentInfo = (cart?: ICartViewModel, paymentOption?: IPaymentOption) => {
  if ((cart?.orderLines?.length ?? 0) > 0) {
    const analytics =
      cart?.orderLines?.map(x => x.itemAnalyticsModel).filter(x => x !== undefined) ?? [];

    trackEvent({
      eventName: AnalyticsEventName.AddPaymentInfo,
      parameters: {
        ...generateEcommerceObject({
          items: ConvertCartToItemAnalyticsModels(cart),
          value: cart?.subTotal,
          currency: Currency.USD,
          coupon: analytics.find(x => x.coupon)?.coupon ?? '',
          payment_type: paymentOption?.cardType
        }),
        stored_instrument: paymentOption ? paymentOption.sk !== -1 : false,
        save_for_future_use: paymentOption?.saveForFutureUse ?? false,
        default: paymentOption?.isDefault ?? false
      }
    });
  }
};

export const trackAddAddressInfo = (
  cart?: ICartViewModel,
  type?: 'billing' | 'shipping',
  isDefault?: boolean,
  isStoredAddress?: boolean
) => {
  if ((cart?.orderLines?.length ?? 0) > 0) {
    const analytics =
      cart?.orderLines?.map(x => x.itemAnalyticsModel).filter(x => x !== undefined) ?? [];

    trackEvent({
      eventName:
        type === 'shipping'
          ? AnalyticsEventName.AddShippingInfo
          : AnalyticsEventName.AddBillingInfo,
      parameters: {
        ...generateEcommerceObject({
          items: ConvertCartToItemAnalyticsModels(cart),
          value: cart?.subTotal,
          currency: Currency.USD,
          coupon: analytics.find(x => x.coupon)?.coupon ?? '',
          ...(type === 'shipping' && { shipping_tier: 'standard' }),
          ...(type === 'billing' && { payment_type: 'CreditCard CyberSource' })
        }),
        default: isDefault ?? false,
        stored_address: isStoredAddress ?? true
      }
    });
  }
};

export const trackApplyPromoCode = (
  cart?: ICartViewModel,
  context?: AnalyticsContext,
  success?: boolean,
  discount?: number
) => {
  if ((cart?.orderLines?.length ?? 0) > 0) {
    const analytics =
      cart?.orderLines?.map(x => x.itemAnalyticsModel).filter(x => x !== undefined) ?? [];

    trackEvent({
      eventName: AnalyticsEventName.ApplyCodeStart,
      parameters: {
        ...generateEcommerceObject({
          items:
            analytics.map((x, index) => ConvertItemAnalyticsModelBase(x, undefined, index)) ?? [],
          coupon: analytics.find(x => x.coupon)?.coupon ?? ''
        }),
        code_success: success ?? false,
        discount: discount ?? 0,
        context: context ?? ''
      }
    });
  }
};

export const trackPlaceOrderEvent = (event: PlaceOrderEvent) => {
  if ((event.cart?.orderLines?.length ?? 0) > 0) {
    const analytics =
      event.cart?.orderLines?.map(x => x.itemAnalyticsModel).filter(x => x !== undefined) ?? [];

    trackEvent({
      eventName: AnalyticsEventName.Purchase,
      parameters: {
        ...generateEcommerceObject({
          items: ConvertCartToItemAnalyticsModels(event.cart),
          transaction_id: event.response?.orderNumber || '',
          value: event.cart?.total,
          tax: event.cart?.taxTotal,
          shipping: event.cart?.shippingCost,
          currency: Currency.USD,
          coupon: analytics.find(x => x.coupon)?.coupon ?? ''
        }),
        upkeep: event.upkeep ?? false,
        poec: event.cart?.hasPoec ?? false,
        international_shipping: false,
        rush_shipping_required: false,
        shipping_tier: 'Standard',
        payment_type: 'CreditCard CyberSource',
        shipping_city: event.shippingAddress?.city ?? '',
        shipping_state: event.shippingAddress?.state ?? '',
        shipping_country: event.shippingAddress?.country ?? '',
        shipping_zip: event.shippingAddress?.zip ?? '',
        shipping_zip4: event.shippingAddress?.plus4 ?? '',
        shipping_address_category: event.shippingAddress?.addressCategory ?? '',
        stored_instrument: event.isStoredInstrument ?? false,
        save_for_future_use: event.saveForFutureUse ?? false,
        total_net_price: event.cart?.total ?? '',
        total_discount: event.cart?.discountValue ?? '',
        purchase_source: 'Checkout',
        zero_dollar_purchase: !event.isPaymentRequired,
        upkeep_service_option: event.cart?.hasUpkeepItems ?? false,
        context: event.context || ''
      }
    });
  }
};

export const trackViewItemEvent = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  if (itemAnalyticsModel) {
    trackEvent({
      eventName: AnalyticsEventName.ViewItem,
      parameters: generateEcommerceObject(
        ConvertItemAnalyticsModel(itemAnalyticsModel, undefined, itemListAnalyticsModel, itemIndex)
      )
    });
  }
};

export const trackViewItemListEvent = (
  listId: string,
  listName: string,
  itemsAnalyticsModel?: ItemAnalyticsModel[],
  context?: AnalyticsContext,
  listStartIndex: number = 0
) => {
  if (itemsAnalyticsModel && itemsAnalyticsModel?.length > 0) {
    trackEvent({
      eventName: AnalyticsEventName.ViewItemList,
      parameters: {
        ...generateEcommerceObject({
          item_list_id: listId,
          item_list_name: listName,
          items: itemsAnalyticsModel?.map((item, index) =>
            ConvertItemAnalyticsModelBase(
              item,
              {
                listId: listId,
                listName: listName
              },
              listStartIndex + index
            )
          )
        }),
        ...(context && { context })
      }
    });
  }
};

export const trackAddWishListEvent = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  context?: AnalyticsContext,
  itemListAnalyticsModel?: ItemListAnalyticsModel
) => {
  if (itemAnalyticsModel) {
    trackEvent({
      eventName: AnalyticsEventName.AddToWishlist,
      parameters: {
        ...generateEcommerceObject(
          ConvertItemAnalyticsModel(itemAnalyticsModel, 1, itemListAnalyticsModel)
        ),
        context: context || ''
      }
    });
  }
};

export const trackRemoveWishListEvent = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  context?: AnalyticsContext,
  bulk: boolean = false,
  itemListAnalyticsModel?: ItemListAnalyticsModel
) => {
  if (itemAnalyticsModel) {
    trackEvent({
      eventName: AnalyticsEventName.RemoveFromWishlist,
      parameters: {
        context: context || '',
        bulk,
        item_list_id: itemListAnalyticsModel?.listId || '',
        item_list_name: itemListAnalyticsModel?.listName || '',
        items: [ConvertItemAnalyticsModelBase(itemAnalyticsModel, itemListAnalyticsModel)]
      }
    });
  }
};

export const trackBulkRemoveWishListEvent = (
  itemsAnalyticsModel?: ItemAnalyticsModel[],
  context?: AnalyticsContext,
  itemListAnalyticsModel?: ItemListAnalyticsModel
) => {
  if (itemsAnalyticsModel && itemsAnalyticsModel.length > 0) {
    trackEvent({
      eventName: AnalyticsEventName.BulkRemoveFromWishlist,
      parameters: {
        context: context || '',
        item_list_id: itemListAnalyticsModel?.listId || '',
        item_list_name: itemListAnalyticsModel?.listName || '',
        items: itemsAnalyticsModel.map((item, index) =>
          ConvertItemAnalyticsModelBase(item, itemListAnalyticsModel, index)
        )
      }
    });
  }
};

export const trackViewPromotionEvent = (
  promotion: PromoEvent,
  itemAnalyticsModel?: ItemAnalyticsModel
) => {
  if (itemAnalyticsModel) {
    trackEvent({
      eventName: AnalyticsEventName.ViewPromotion,
      parameters: {
        ...generateEcommerceObject({
          creative_name: promotion.creative_name,
          creative_slot: promotion.creative_slot,
          promotion_id: promotion.promotion_id,
          promotion_name: promotion.promotion_name,
          items: [ConvertItemAnalyticsModelBase(itemAnalyticsModel)],
          location_id: promotion.location_id
        }),
        context: promotion.context || ''
      }
    });
  }
};

export const trackSelectPromotionEvent = (
  promotion: PromoEvent,
  itemAnalyticsModel?: ItemAnalyticsModel
) => {
  if (itemAnalyticsModel) {
    trackEvent({
      eventName: AnalyticsEventName.SelectPromotion,
      parameters: {
        ...generateEcommerceObject({
          creative_name: promotion.creative_name,
          creative_slot: promotion.creative_slot,
          promotion_id: promotion.promotion_id,
          promotion_name: promotion.promotion_name,
          items: [ConvertItemAnalyticsModelBase(itemAnalyticsModel)],
          location_id: promotion.location_id
        }),
        context: promotion.context || ''
      }
    });
  }
};

export const trackSelectCheckoutEvent = (context?: AnalyticsContext) => {
  if (context) {
    trackEvent({
      eventName: AnalyticsEventName.SelectCheckout,
      parameters: {
        context: context
      }
    });
  }
};

export const trackRemoveFromCartEvent = (item?: OrderLineViewModel, context?: AnalyticsContext) => {
  if (item && context) {
    const analytics = item.itemAnalyticsModel ? [item.itemAnalyticsModel] : [];

    trackEvent({
      eventName: AnalyticsEventName.RemoveFromCart,
      parameters: {
        ...generateEcommerceObject({
          items:
            analytics.map((x, index) => ConvertItemAnalyticsModelBase(x, undefined, index)) ?? [],
          value: item.discountPrice ?? 0,
          currency: Currency.USD
        }),
        context: context
      }
    });
  }
};
